import { applySnapshot, getSnapshot, types } from 'mobx-state-tree'
import {
  EhealthUserLoginCommand,
  LoginCommand,
  UserInfoModel,
} from 'stores/models'
import { apiV1 } from 'core/requests'
import { fromPromise, FULFILLED } from 'mobx-utils'
import { when } from 'mobx'

const AuthStore = types
  .model('AuthStore')
  .props({
    _user: types.optional(UserInfoModel, {}),
    _isLoading: types.optional(types.boolean, false),
    _isAuth: types.optional(types.boolean, false),
    _isRememberMe: types.optional(types.boolean, false),
    _isStupid: types.optional(types.boolean, false),
    _loginTries: types.maybeNull(types.optional(types.number, 0)),
    _userName: types.optional(types.string, ''),
    _isPending: types.optional(types.boolean, false),
    _isBlocked: types.optional(types.boolean, false),
  })
  .actions(self => {
    const ACCOUNT_API = '/api/account'

    const setUser = userData => {
      applySnapshot(self._user, userData)
    }

    const setIsAuth = isAuth => {
      self._isAuth = isAuth
    }

    const setIsRememberMe = isRememberMe => {
      self._isRememberMe = isRememberMe
    }

    const signOut = () => {
      setIsAuth(false)
      setUser(UserInfoModel.create())
    }

    const setIsLoading = isLoading => {
      self._isLoading = isLoading
    }

    const setIsStupid = isStupid => {
      self._isStupid = isStupid
    }

    const setLoginTries = number => {
      self._loginTries = number
    }

    const setUserName = name => {
      self._userName = name
    }

    const setIsPending = isPending => {
      self._isPending = isPending
    }

    const setIsBlocked = isBlocked => {
      self._isBlocked = isBlocked
    }

    const signIn = userData => {
      const loginRequest = fromPromise(
        apiV1.post(`${ACCOUNT_API}/login`, LoginCommand.create(userData)),
      )

      when(() =>
        loginRequest.case({
          fulfilled: response => {
            setUser({
              ...response.data,
            })
            return true
          },
        }),
      )
      return loginRequest
    }

    const signInAsEhealthUser = userData => {
      const loginRequest = fromPromise(
        apiV1.post(
          `${ACCOUNT_API}/login/ehealth`,
          EhealthUserLoginCommand.create(userData),
        ),
      )

      when(() =>
        loginRequest.case({
          fulfilled: response => {
            setUser({
              ...response.data,
            })
            return true
          },
        }),
      )
      return loginRequest
    }

    const refreshToken = (_data = {}) => {
      setUser(UserInfoModel.create())

      const prom = fromPromise(apiV1.post(`${ACCOUNT_API}/refreshtoken`, _data))

      when(
        () => prom.state === FULFILLED,
        () => {
          prom.case({
            fulfilled: response => {
              setUser({
                ...response.data,
              })
            },
          })
        },
      )

      return prom
    }

    const getTundukAuthUri = () => {
      return fromPromise(apiV1.get(`${ACCOUNT_API}/login/tunduk`))
    }

    const sendRequestForSupport = async data =>
      apiV1.post(`${ACCOUNT_API}/support`, data)

    const sendCaptcha = async data => {
      return apiV1.post(`${ACCOUNT_API}/sendcaptcha`, data)
    }

    const sendTundukAuth = async data => {
      const loginRequest = fromPromise(
        apiV1.post(`${ACCOUNT_API}/tunduk/login`, data),
      )

      when(() =>
        loginRequest.case({
          fulfilled: response => {
            setUser({
              ...response.data,
            })
            return true
          },
        }),
      )
      return loginRequest
    }

    return {
      signIn,
      signInAsEhealthUser,
      signOut,
      setIsLoading,
      setIsAuth,
      setUser,
      setIsRememberMe,
      sendRequestForSupport,
      refreshToken,
      setIsStupid,
      setLoginTries,
      sendCaptcha,
      setUserName,
      getTundukAuthUri,
      sendTundukAuth,
      setIsPending,
      setIsBlocked,
    }
  })
  .views(self => ({
    get user() {
      return getSnapshot(self._user)
    },
    get isAuth() {
      return self._isAuth
    },
    get isLoading() {
      return self._isLoading
    },
    get isRememberMe() {
      return self._isRememberMe
    },
    get tundukAuthUri() {
      return self._tundukAuthUri
    },
    get isPending() {
      return self._isPending
    },
    get isBlocked() {
      return self._isBlocked
    },
  }))

export default AuthStore
